import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarRange, FileText, Users, Network, SlidersHorizontal } from 'lucide-react';
import { api, type ApiError } from '../api';
import { cn } from '../lib/utils';
import ManageYears from './manage/Years';
import ManagePresentations from './manage/Presentations';
import ManageJudges from './manage/Judges';
import ManageAllocations from './manage/Allocations';
import ManageCriteria from './manage/Criteria';

export interface Year {
  id: number;
  label: string;
  year: number | null;
  is_current: boolean;
  judge_count: number;
  presentation_count: number;
  parent_year_id?: number | null;
  round_no?: number | null;
}

const TABS = [
  { key: 'years', label: 'Years', Icon: CalendarRange },
  { key: 'presentations', label: 'Presentations', Icon: FileText },
  { key: 'judges', label: 'Judges', Icon: Users },
  { key: 'allocations', label: 'Allocations', Icon: Network },
  { key: 'criteria', label: 'Rubrics', Icon: SlidersHorizontal },
] as const;

export default function Manage() {
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('years');
  const [years, setYears] = useState<Year[]>([]);
  const [editYearId, setEditYearId] = useState<number | null>(null);

  async function loadYears() {
    try {
      const d = await api<{ years: Year[] }>('admin-years');
      setYears(d.years);
      setEditYearId((prev) => prev ?? d.years.find((y) => y.is_current)?.id ?? d.years[0]?.id ?? null);
    } catch (e) {
      toast.error((e as ApiError).message || 'Failed to load years');
    }
  }
  useEffect(() => {
    void loadYears();
  }, []);

  return (
    <main className="mx-auto max-w-4xl space-y-6 px-4 py-6">
      <h1 className="text-xl font-bold tracking-tight">Manage</h1>

      <div className="flex flex-wrap gap-1 rounded-lg bg-secondary p-1">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="size-4" />
            {label}
          </button>
        ))}
      </div>

      {tab !== 'years' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Editing year:</span>
          <select
            value={editYearId ?? ''}
            onChange={(e) => setEditYearId(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-card px-2 text-sm font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.label}
                {y.is_current ? ' • current' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {tab === 'years' && <ManageYears years={years} reload={loadYears} />}
      {tab === 'presentations' && editYearId && <ManagePresentations yearId={editYearId} onChange={loadYears} />}
      {tab === 'judges' && editYearId && <ManageJudges yearId={editYearId} onChange={loadYears} />}
      {tab === 'allocations' && editYearId && <ManageAllocations yearId={editYearId} />}
      {tab === 'criteria' && editYearId && <ManageCriteria yearId={editYearId} />}
    </main>
  );
}
